import React, { useEffect, useState } from 'react';
import Dialog from 'src/components/common/Dialog';
import Button from 'src/components/controls/forms/redesign/FormButton';
import { useForm, UseFormMethods } from 'react-hook-form';
import RHFSelect from '../../controls/tempRHFControls/RHFSelect';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import DialogList from 'src/dialogs';
import { AnyObject } from 'final-form';
import FindClientDialog from 'src/components/client-project/dialogs/FindClientDialog';
import ClientNotesDialog from './ClientNotesDialog';
import TsdiClient from 'src/models/AP/Client';
import { useArServiceCtx } from '../AccountsReceivableServiceContext';
import { AppContext } from 'src/providers/AppContextProvider';
import { IAppContextState } from 'src/reducers/AppContext';
import PillToggle from 'src/components/controls/PillToggle';

interface IClientNote {
    id?: number;
    date?: string;
    by?: string;
    text: string;
    active?: boolean;
    createdDate?: string;
    deactiveDate?: string;
    noteTypeDescription?: string;
}

interface IArReportDialogProps {
    dialogName: string;
    handleReport?: (data: AnyObject) => void;
    handleNotes?: (data: AnyObject) => void;
    hideDialog: () => void;
    showDialog: (dialogName: DialogList) => void;
    reportOptions?: Array<{ value: string | number, label: string }>;
    typeOptions?: Array<{ value: string | number, label: string }>;
    agingTransOptions?: Array<{ value: string | number, label: string }>;
    workInProgressOptions?: Array<{ value: string | number, label: string }>;
    orderByOptions?: Array<{ value: string | number, label: string }>;
    managers?: Array<{ value: string | number, label: string }>;
}

const labelStyle: React.CSSProperties = { whiteSpace: 'nowrap', width: '9rem' };

// usp_ARAgingReport's own aging-window codes (C/60/90/90+/30+/60+) - see
// CreateProcedure_ARAgingReport.sql - don't line up 1:1 with this dialog's option values.
const AGING_TRANS_MAP: Record<string, string> = {
    ALL: '',
    CURRENT: 'C',
    '31_60': '60',
    '31_90_PLUS': '30+',
    '61_90': '90',
    '61_90_PLUS': '60+',
    '90_PLUS': '90+'
};

const WIP_MAP: Record<string, string> = {
    ALL: '',
    WIP_ONLY: 'Y',
    WITHOUT_WIP: 'N'
};

const todayIsoDate = () => {
    const now = new Date();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    return `${now.getFullYear()}-${mm}-${dd}`;
};

// usp_ARAgingReport parses rpt_date with TRY_CONVERT(date, @rpt_date, 101) - MM/DD/YYYY - while
// the native <input type="date"> field this dialog uses always emits ISO (YYYY-MM-DD).
const isoDateToMMDDYYYY = (isoDate: string) => {
    if (!isoDate) {
        return '';
    }
    const [year, month, day] = isoDate.split('-');
    return `${month}/${day}/${year}`;
};

// reportOptions.type (All/Client/Office/Project) is a pre-existing field left in place but NOT
// wired to usp_ARAgingReport's @type - that parameter actually means Expense vs Leasehold
// transactions, an unrelated concept the new "Account Type" field below covers instead.
const buildArAgingReportPayload = (reportOptions: AnyObject) => ({
    reportDate: isoDateToMMDDYYYY(reportOptions?.reportDate),
    type: reportOptions?.accountType || '',
    clientNumber: reportOptions?.clientNumber || '',
    office: reportOptions?.office || '',
    summary: reportOptions?.summary || 'N',
    orderBy: reportOptions?.orderBy || 'NUMBER',
    displayAddress: reportOptions?.displayAddress || 'N',
    projectNumber: reportOptions?.projectNumber || '',
    agingTrans: AGING_TRANS_MAP[reportOptions?.agingTrans] ?? '',
    managerNo: reportOptions?.manager && reportOptions.manager !== 'ALL' ? reportOptions.manager : '',
    wip: WIP_MAP[reportOptions?.workInProgress] ?? ''
});

// usp_ARLedgerV3LegalReport's @type uses Oracle's literal 'A'/'E'/'L' contract (no blank-for-All,
// unlike usp_ARAgingReport) - so "All" from the same Account Type dropdown maps to 'A' here
// rather than ''. Notes maps from the existing "Display Notes?" pill (already in this dialog for
// the AR_CLIENT_NOTES dialog's own use, but its Y/N value doubles as this report's own P_NOTES).
const buildArLedgerV3LegalReportPayload = (reportOptions: AnyObject) => ({
    reportDate: isoDateToMMDDYYYY(reportOptions?.reportDate),
    type: reportOptions?.accountType || 'A',
    clientNumber: reportOptions?.clientNumber || '',
    office: reportOptions?.office || '',
    summary: reportOptions?.summary || 'N',
    orderBy: reportOptions?.orderBy || 'NUMBER',
    displayAddress: reportOptions?.displayAddress || 'N',
    projectNumber: reportOptions?.projectNumber || '',
    agingTrans: AGING_TRANS_MAP[reportOptions?.agingTrans] ?? '',
    managerNo: reportOptions?.manager && reportOptions.manager !== 'ALL' ? reportOptions.manager : '',
    wip: WIP_MAP[reportOptions?.workInProgress] ?? '',
    notes: reportOptions?.displayNotes || 'N'
});

// Per-label marginRight tuned to each label's text length so the differently-worded
// labels in the same row still land their inputs/selects at a common x position.
const clientNoLabelStyle: React.CSSProperties = { whiteSpace: 'nowrap', marginRight: '1.5rem' };
const projectNoLabelStyle: React.CSSProperties = { whiteSpace: 'nowrap', marginRight: '1rem' };
const officeLabelStyle: React.CSSProperties = { whiteSpace: 'nowrap', marginRight: '3.75rem' };
const summaryLabelStyle: React.CSSProperties = { whiteSpace: 'nowrap', marginRight: '3rem' };
const displayNotesLabelStyle: React.CSSProperties = { whiteSpace: 'nowrap', marginRight: '0.25rem' };
const displayAddressLabelStyle: React.CSSProperties = { whiteSpace: 'nowrap', marginRight: '0.1rem' };

interface IYesNoPillFieldProps {
    name: string;
    label: string;
    labelStyle?: React.CSSProperties;
    bodyStyle?: React.CSSProperties;
    defaultVal?: 'Y' | 'N';
    register: UseFormMethods['register'];
    watch: UseFormMethods['watch'];
    setValue: UseFormMethods['setValue'];
}

// Yes/No fields kept as 'Y'/'N' string values (matching the legacy report-parameter contract)
// but presented as a pill toggle rather than a dropdown - the actual field is a hidden input
// react-hook-form registers normally; the toggle just flips its value via setValue.
const YesNoPillField = (props: IYesNoPillFieldProps) => {
    const value = props.watch(props.name, props.defaultVal ?? 'N');
    const checked = value === 'Y';

    return (
        <div className="form-field" style={props.bodyStyle}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
                <label style={{ ...props.labelStyle }}>{props.label}</label>
                <input type="hidden" name={props.name} ref={props.register} defaultValue={props.defaultVal ?? 'N'} />
                <PillToggle
                    checked={checked}
                    onChange={() => props.setValue(props.name, checked ? 'N' : 'Y')}
                />
            </div>
        </div>
    );
};

const ArReportDialog = (props: IArReportDialogProps) => {
    const { register, errors, getValues, reset, watch, setValue } = useForm();
    const { getClientNotes, createClientNote, updateClientNote, setClientNoteActive, getArAgingReport, getArLedgerV3LegalReport } = useArServiceCtx();
    const appContext = React.useContext<IAppContextState>(AppContext);
    const [clientNoError, setClientNoError] = useState<string>('');
    const [notesClientNumber, setNotesClientNumber] = useState<string | number>(undefined);
    const [clientNotes, setClientNotes] = useState<Array<IClientNote>>([]);
    // Only trustworthy while it still matches what's in the Client No. field - cleared the
    // moment the user hand-edits the number, since the name would no longer correspond to it.
    const [selectedClient, setSelectedClient] = useState<TsdiClient>(undefined);
    // Drives the OK button's spinner (FormButton's `loading` prop) while the SSRS round-trip is
    // in flight - the dialog stays open the whole time with no other feedback otherwise, same
    // gap InvoiceOfficeBreakdownDialog.tsx had.
    const [reportLoading, setReportLoading] = useState<boolean>(false);

    useEffect(() => {
        onCreate();
    }, []);

    const onCreate = () => {
        console.log('ArReportDialog onCreate()');
    };

    const OKClicked = async () => {
        let data = getValues();
        const reportOptions = data?.reportOptions;

        // Only two of the 8 Report dropdown options are wired up so far:
        // - "A/R Aging" (ILEDGER_V2.RDF -> usp_ARAgingReport/ARAging.rdl)
        // - "A/R Detailed Aging - Post Date/Legal" (ARLEDGER_V3_LEGAL.RDF ->
        //   usp_ARLedgerV3LegalReport/ARLedgerV3Legal.rdl)
        // The remaining 6 map to different, not-yet-built RDFs.
        if (reportOptions?.report !== 'AGING' && reportOptions?.report !== 'DETAILED_AGING_POST_LEGAL') {
            window.alert('This report has not been implemented yet.');
            return;
        }

        setReportLoading(true);

        if (reportOptions?.report === 'AGING') {
            await getArAgingReport(buildArAgingReportPayload(reportOptions));
        } else {
            await getArLedgerV3LegalReport(buildArLedgerV3LegalReportPayload(reportOptions));
        }

        setReportLoading(false);
        props.handleReport?.(reportOptions);
        props.hideDialog();
    };

    // Server orders by NT_DATE DESC and has the full picture (real id, CreatedDate,
    // NoteTypeDescription join) - re-pulled after every mutation rather than trying to keep a
    // locally-built stub in sync, so a freshly saved/edited/toggled note always lands correctly
    // sorted with complete metadata instead of a partial client-side guess.
    const refreshClientNotes = async (clientNumber: number) => {
        const result = await getClientNotes(clientNumber);
        const notes: Array<AnyObject> = Array.isArray(result) ? result : [];
        setClientNotes(notes.map((n) => ({
            id: n?.id,
            text: n?.text,
            by: n?.createdBy,
            // NT_DATE - when the text was last actually saved (distinct from CreatedDate,
            // the row's birth) - the more relevant timestamp to show alongside "by".
            date: n?.modifiedDate,
            active: n?.active,
            createdDate: n?.createdDate,
            deactiveDate: n?.deactiveDate,
            noteTypeDescription: n?.noteTypeDescription
        })));
    };

    const NotesClicked = async () => {
        let data = getValues();
        const clientNumber = data?.reportOptions?.clientNumber;

        if (!clientNumber) {
            setClientNoError('Client # required for Notes');
            return;
        }

        setClientNoError('');
        setNotesClientNumber(clientNumber);
        props.handleNotes?.(data?.reportOptions);

        await refreshClientNotes(Number(clientNumber));

        props.showDialog(DialogList.AR_CLIENT_NOTES);
    };

    const handleAddClientNote = async (text: string) => {
        if (!notesClientNumber) {
            return;
        }

        await createClientNote(Number(notesClientNumber), {
            text,
            createdBy: appContext?.currentUser?.username
        });

        await refreshClientNotes(Number(notesClientNumber));
    };

    const handleUpdateClientNote = async (noteId: number, text: string) => {
        await updateClientNote(noteId, text);

        if (notesClientNumber) {
            await refreshClientNotes(Number(notesClientNumber));
        }
    };

    const handleToggleClientNoteActive = async (noteId: number, active: boolean) => {
        await setClientNoteActive(noteId, active);

        if (notesClientNumber) {
            await refreshClientNotes(Number(notesClientNumber));
        }
    };

    const handleClientSelected = (client: TsdiClient) => {
        // The Client No. field is unmounted right now (AR_REPORT is hidden while Find Client is
        // showing, since Dialog unmounts its children when hidden) - setValue only affects a
        // field that's currently registered, so it'd silently no-op here. reset() instead updates
        // react-hook-form's stored default values, which the field picks up once it remounts.
        reset({ reportOptions: { ...getValues()?.reportOptions, clientNumber: client?.clientNumber } });
        setClientNoError('');
        setSelectedClient(client);
    };

    const closeFindClientAndReturn = () => {
        props.hideDialog();
        // FindClientDialog calls whatever's passed as `hideDialog` right after selecting a
        // client (or on Cancel) - since only one dialog can be active globally at a time,
        // reopening AR_REPORT has to happen on the next tick or this call clears it right back out.
        setTimeout(() => props.showDialog(DialogList.AR_REPORT), 0);
    };

    const ExcelClicked = () => {
        let data = getValues();

        props.handleReport?.(data?.reportOptions);
        props.hideDialog();
        props.showDialog(DialogList.EXCEL_FILE);
    };

    return (
        <>
        <Dialog title="A/R Reports" dialogName={props.dialogName} isExitButtonInvisible={true} bodyStyle={{ width: '30%' }}>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
                <form style={{ width: '90%' }}>
                    <RHFInputBox
                        label="Report Date"
                        labelOnLeft={true}
                        labelStyle={labelStyle}
                        name={`reportOptions.reportDate`}
                        type="date"
                        register={register}
                        errors={errors}
                        defaultVal={todayIsoDate()}
                        style={{ width: '100%' }}
                        bodyStyle={{ width: '100%' }}
                    />
                    <RHFSelect
                        label="Report"
                        labelOnLeft={true}
                        labelStyle={labelStyle}
                        name={`reportOptions.report`}
                        options={!!props?.reportOptions ? props.reportOptions : [
                            { value: 'AGING', label: 'A/R Aging' },
                            { value: 'AGING_SUMMARY', label: 'A/R Aging Summary' },
                            { value: 'AGING_SUMMARY_GL_DATE', label: 'A/R Aging Summary - GL Date' },
                            { value: 'DETAILED_AGING', label: 'A/R Detailed Aging' },
                            { value: 'DETAILED_AGING_POST_LEGAL', label: 'A/R Detailed Aging - Post Date/Legal' },
                            { value: 'DETAILED_AGING_POST_LEGAL_PAY_PERIOD', label: 'A/R Detailed Aging - Post Date/Legal with Pay Period' },
                            { value: 'DETAILED_AGING_LEGAL_SIZE', label: 'A/R Detailed Aging - Legal Size' },
                            { value: 'EMAIL', label: 'E-Mail Report' },
                        ]}
                        register={register}
                        errors={errors}
                        defaultVal="AGING"
                        style={{ width: '100%' }}
                        bodyStyle={{ width: '100%' }}
                    />
                    <RHFSelect
                        label="Type"
                        labelOnLeft={true}
                        labelStyle={labelStyle}
                        name={`reportOptions.type`}
                        options={!!props?.typeOptions ? props.typeOptions : [
                            { value: 'ALL', label: 'All' },
                            { value: 'CLIENT', label: 'Client' },
                            { value: 'OFFICE', label: 'Office' },
                            { value: 'PROJECT', label: 'Project' },
                        ]}
                        register={register}
                        errors={errors}
                        defaultVal="ALL"
                        style={{ width: '100%' }}
                        bodyStyle={{ width: '100%' }}
                    />
                    <RHFSelect
                        label="Account Type"
                        labelOnLeft={true}
                        labelStyle={labelStyle}
                        name={`reportOptions.accountType`}
                        options={[
                            { value: '', label: 'All' },
                            { value: 'E', label: 'Expense' },
                            { value: 'L', label: 'Leasehold' },
                        ]}
                        register={register}
                        errors={errors}
                        defaultVal=""
                        style={{ width: '100%' }}
                        bodyStyle={{ width: '100%' }}
                    />
                    <RHFSelect
                        label="Aging Transactions"
                        labelOnLeft={true}
                        labelStyle={{ whiteSpace: 'nowrap', marginRight: '1rem' }}
                        name={`reportOptions.agingTrans`}
                        options={!!props?.agingTransOptions ? props.agingTransOptions : [
                            { value: 'ALL', label: 'All Transactions' },
                            { value: 'CURRENT', label: 'Current' },
                            { value: '31_60', label: '31-60 Days' },
                            { value: '31_90_PLUS', label: '31-90+ Days' },
                            { value: '61_90', label: '61-90 Days' },
                            { value: '61_90_PLUS', label: '61-90+ Days' },
                            { value: '90_PLUS', label: '90+ Days' },
                        ]}
                        register={register}
                        errors={errors}
                        defaultVal="ALL"
                        style={{ width: '100%' }}
                        bodyStyle={{ width: '100%' }}
                    />
                    <RHFSelect
                        label="Work in Progress"
                        labelOnLeft={true}
                        labelStyle={{ whiteSpace: 'nowrap', marginRight: '2rem' }}
                        name={`reportOptions.workInProgress`}
                        options={!!props?.workInProgressOptions ? props.workInProgressOptions : [
                            { value: 'ALL', label: 'All Transactions' },
                            { value: 'WIP_ONLY', label: 'WIP Transactions Only' },
                            { value: 'WITHOUT_WIP', label: 'Without WIP Transactions' },
                        ]}
                        register={register}
                        errors={errors}
                        defaultVal="ALL"
                        style={{ width: '90%' }}
                        bodyStyle={{ width: '100%' }}
                    />
                    <RHFSelect
                        label="Manager"
                        labelOnLeft={true}
                        labelStyle={labelStyle}
                        name={`reportOptions.manager`}
                        options={!!props?.managers ? props.managers : [
                            { value: 'ALL', label: 'ALL' },
                        ]}
                        register={register}
                        errors={errors}
                        defaultVal="ALL"
                        style={{ width: '100%' }}
                        bodyStyle={{ width: '100%' }}
                    />
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <RHFInputBox
                            label="Client No."
                            labelOnLeft={true}
                            labelStyle={clientNoLabelStyle}
                            name={`reportOptions.clientNumber`}
                            type="number"
                            register={register}
                            errors={errors}
                            style={{ width: '100%' }}
                            bodyStyle={{ width: '48%' }}
                            className={clientNoError ? 'error-field' : ''}
                            onChange={() => {
                                if (clientNoError) {
                                    setClientNoError('');
                                }
                                setSelectedClient(undefined);
                            }}
                            onDoubleClick={() => props.showDialog(DialogList.FIND_CLIENT)}
                        />
                        <YesNoPillField
                            label="Display Address ?"
                            labelStyle={displayAddressLabelStyle}
                            name={`reportOptions.displayAddress`}
                            register={register}
                            watch={watch}
                            setValue={setValue}
                            defaultVal="N"
                            bodyStyle={{ width: '48%' }}
                        />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <RHFInputBox
                            label="Project No."
                            labelOnLeft={true}
                            labelStyle={projectNoLabelStyle}
                            name={`reportOptions.projectNumber`}
                            type="number"
                            register={register}
                            errors={errors}
                            style={{ width: '100%' }}
                            bodyStyle={{ width: '48%' }}
                        />
                        <YesNoPillField
                            label="Summary ?"
                            labelStyle={summaryLabelStyle}
                            name={`reportOptions.summary`}
                            register={register}
                            watch={watch}
                            setValue={setValue}
                            defaultVal="N"
                            bodyStyle={{ width: '48%' }}
                        />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <RHFInputBox
                            label="Office"
                            labelOnLeft={true}
                            labelStyle={officeLabelStyle}
                            name={`reportOptions.office`}
                            type="number"
                            register={register}
                            errors={errors}
                            style={{ width: '100%' }}
                            bodyStyle={{ width: '48%' }}
                        />
                        <YesNoPillField
                            label="Display Notes?"
                            labelStyle={displayNotesLabelStyle}
                            name={`reportOptions.displayNotes`}
                            register={register}
                            watch={watch}
                            setValue={setValue}
                            defaultVal="N"
                            bodyStyle={{ width: '48%' }}
                        />
                    </div>
                    <RHFSelect
                        label="Order by ?"
                        labelOnLeft={true}
                        labelStyle={labelStyle}
                        name={`reportOptions.orderBy`}
                        options={!!props?.orderByOptions ? props.orderByOptions : [
                            { value: 'NUMBER', label: 'Number' },
                            { value: 'NAME', label: 'Name' },
                        ]}
                        register={register}
                        errors={errors}
                        defaultVal="NUMBER"
                        style={{ width: '100%' }}
                        bodyStyle={{ width: '100%' }}
                    />
                </form>
            </div>
            <div className="form-row" style={{ marginTop: '2rem', justifyContent: 'center' }}>
                <Button
                    className="blue-hover-button"
                    style={{ width: '18%', marginRight: '1rem' }}
                    text={reportLoading ? 'Generating' : 'OK'}
                    disabled={reportLoading}
                    loading={reportLoading}
                    onClick={() => !reportLoading && OKClicked()}
                />
                <Button
                    className="blue-hover-button"
                    style={{ width: '18%', marginRight: '1rem' }}
                    text={'Notes'}
                    disabled={reportLoading}
                    onClick={() => NotesClicked()}
                />
                <Button
                    className="blue-hover-button"
                    style={{ width: '18%', marginRight: '1rem' }}
                    text={'Excel'}
                    disabled={reportLoading}
                    onClick={() => ExcelClicked()}
                />
                <Button
                    className="blue-hover-button"
                    style={{ width: '18%', marginRight: '1rem' }}
                    text={'Cancel'}
                    disabled={reportLoading}
                    onClick={() => props.hideDialog()}
                />
            </div>
            {clientNoError && (
                <div style={{ color: 'red', textAlign: 'center', marginTop: '0.5rem' }}>
                    {clientNoError}
                </div>
            )}
        </Dialog>
        <FindClientDialog
            dialogName={DialogList.FIND_CLIENT}
            hideDialog={closeFindClientAndReturn}
            setSelectedClient={handleClientSelected}
        />
        <ClientNotesDialog
            dialogName={DialogList.AR_CLIENT_NOTES}
            hideDialog={props.hideDialog}
            showDialog={props.showDialog}
            returnDialogName={DialogList.AR_REPORT}
            clientNumber={notesClientNumber}
            clientName={selectedClient?.clientName}
            notes={clientNotes}
            handleAddNote={handleAddClientNote}
            handleUpdateNote={handleUpdateClientNote}
            handleToggleActive={handleToggleClientNoteActive}
        />
        </>
    );
};

export default ArReportDialog;



// WEBPACK FOOTER //
// ./src/components/accounts-receivable/dialogs/ArReportDialog.tsx